import { Component, Vue } from "vue-property-decorator";

import { BlankPage, Loading, Hint } from "@/components";
import { Reconciliation, Common } from "@/constant";
import { AppropriationModel } from "@/models";
import { TCBReceiptApi } from "@/network/pb/proto";

import { Priv } from "@/util/Priv";
import { DefaultPath } from "@/constant/GroupPermission";

// 特店 - 子項目 (每個特店資訊)
type Store = {
    id: number;
    code: string;
    name: string;
    selected: boolean;
};

// 特店 - 主項目 (統編號碼 + 全選)
type StoreGroup = {
    tidNumber: string;
    selected: boolean;
    storeList: Store[];
};

// 統計種類
type TotalItem = {
    infoType: number; // 種類 [2 => 批號小結, 3 => 端末機號小結, 4 => 合計]

    batchNo: string; // 批號小結
    subCount: number; // 筆數
    subTotal: number; // 小計

    dayTotal: number; // 每日小結

    terminal: string; // 端末機號小結
    terminalCount: number; // 端末機號小結 - 筆數
    terminalTotal: number; // 端末機號小結 - 小計

    count: number; // 合計 - 筆數
    total: number; // 合計 - 總計
};

// 統計支付寶
type AlipayItem = {
    infoType: number; // 種類 [2 => 小計, 3 => 合計]

    count: number; // 小計 / 合計 - 筆數
    outAmount: number; // 小計 / 合計 - 境外交易金額
    amount: number; // 小計 / 合計 - 交易金額
};

// 統計調帳
type MotifyItem = {
    infoType: number; // 種類 [2 => 小計, 3 => 合計]

    count: number; // 小計 / 合計 - 筆數
    amount: number; // 小計 / 合計 - 調整金額
    charge: number; // 小計 / 合計 - 調整手續費
};

// 撥款彙總 查看明細
type AppropriationDetailTotal = { infoType: number; terminalNo: string; count: number; total: number; };
type AppropriationDetailInfo = (TCBReceiptApi.Protobuf.AppropriationInfoCredit | AppropriationDetailTotal)[];
type AppropriationDetail = {
    // 特店代號
    [storeCode: string]: {
        // 端末機號
        [terminalNo: string]: {
            // 撥款日
            [date: string]: {
                // 類別(    1: C信用卡與非金融卡以外的, 2: F金融卡)
                [cardType: string]: AppropriationDetailInfo
            }
        }
    }
};

@Component<ReconciliationAppropriationPrintController>({
    components: {
        BlankPage,
        Hint,
        Loading,
    },
})
export default class ReconciliationAppropriationPrintController extends Vue {
    private isLoading = true; // 控制顯示header

    // 搜尋引擎 - 空用元件
    private periodMap = Reconciliation.PeriodMap;
    private transactionMap = Reconciliation.TransactionMap;
    private noteMap = Reconciliation.NoteMap;
    private cardMap = Reconciliation.CardMap;
    private minDate = "";
    private maxDate = "";

    private searchItem: {
        storeType: number;
        tidNumber: string;
        storeArr: string[];
        type: number;
        periodType: number;
        start: string;
        end: string;
        transactionType: number;
        noteType: number;
        cardType: string[];
        cardNum: string;
        orderNum: string;
        machineCodeStart: string;
        machineCodeEnd: string;
    } = {
            storeType: 0,
            tidNumber: "",
            storeArr: [],
            type: 1,
            periodType: 1,
            start: "",
            end: "",
            transactionType: 0,
            noteType: 0,
            cardType: ["1", "2", "3", "4", "5"],
            cardNum: "",
            orderNum: "",
            machineCodeStart: "",
            machineCodeEnd: "",
        };
    private datePeriodStartMax = "";

    // 選取特約清單 => 是否展開LB
    private storeGroupList: StoreGroup[] = [];
    private isOpenStoreLB = false;

    // filter => 依特店彙總, 依入帳號彙總
    private filterType = "detail";

    // 列表
    private reconciliationList: TCBReceiptApi.Protobuf.IReconciliation[] = [];
    private appropriationMap = TCBReceiptApi.Protobuf.AppropriationMap.create();

    // 明細
    private byCardMap: { [k: string]: (TCBReceiptApi.Protobuf.AppropriationInfoCredit | TotalItem)[] } = {};
    private byMotifyMap: { [k: string]: (TCBReceiptApi.Protobuf.AppropriationInfoMotify | MotifyItem)[] } = {};
    private byAlipayMap: { [k: string]: (TCBReceiptApi.Protobuf.AppropriationInfoAlipay | AlipayItem)[] } = {};

    // 撥款彙總
    private byAppropriationMap: { [k: string]: TCBReceiptApi.Protobuf.AppropriationInfoSummary[] } = {};

    // 卡別彙總
    private cardTypeMap: { [k: string]: TCBReceiptApi.Protobuf.AppropriationSummaryByCardType } = {};
    private creditCardMap: { [k: string]: (TCBReceiptApi.Protobuf.AppropriationInfoSummaryByIssuer | { count: number, amount: number, infoType: number })[] } = {};
    private debitCardMap: { [k: string]: (TCBReceiptApi.Protobuf.AppropriationInfoSummaryByIssuer | { count: number, amount: number, infoType: number })[] } = {};

    // 總計 - 明細 / 依撥款彙總 / 依卡別彙總
    private detailTotalObj: { count: number, total: number } = { count: 0, total: 0 };
    private appropriationTotalObj: { count: number, total: number } = { count: 0, total: 0 };
    private cardTypeTotalObj: { count: number, total: number } = { count: 0, total: 0 };

    // 展開收合
    private isUnfold = false;
    private unfoldList: string[] = [];

    // 展開明細
    private showAllList: string[] = [];

    // 查看明細 => 是否展開LB
    private isOpenDetailLB = false;
    private detailTitle: object = {};
    private detail = TCBReceiptApi.Protobuf.AppropriationInfoCredit.create();
    private detailType = 1; // 預設 1 => 請款, 2 => 撥款

    // 查看明細 => 依撥款彙總的查看
    private appropriationDetail: AppropriationDetail = {};
    private isOpenTableLB = false;
    private batchNoTitle = TCBReceiptApi.Protobuf.Reconciliation.create();
    private batchNoInfo: object = {};
    private tableType = 1; // 預設 1 => 請款, 2 => 撥款

    // LB
    private hintBox: { show: boolean, msg: string } = { show: false, msg: "" };

    public async created() {
        /* 取得最新的權限map並判斷有何權限 */
        await this.setPriv();
        /* 取得最新的權限map並判斷有何權限 */

        const storageItemJson = localStorage.getItem("FrontReconciliationAppropriation") as string;
        if (storageItemJson && storageItemJson !== "") {
            const storageItem = JSON.parse(storageItemJson) as { search: TCBReceiptApi.Protobuf.AppropriationQuery, filterType: string };
            console.log(storageItem);

            const filterType = storageItem.filterType;
            const searchItem = storageItem.search;
            const appropriationQuery = TCBReceiptApi.Protobuf.AppropriationQuery.encode(searchItem).finish();

            this.changeFilter(filterType);
            await this.searchByCondition(appropriationQuery);
            window.print();
        } else {
            this.openHint(Common.ErrorCodeMap[Common.ErrorHintMap.PrintEmptyData]);
            return false;
        }
    }

    /* LB */
    private openHint(msg: string) {
        this.hintBox.msg = msg;
        this.hintBox.show = true;
    }
    private closeHint() {
        this.hintBox.msg = "";
        this.hintBox.show = false;
    }
    /* LB */

    /* 搜尋 */
    private async searchByCondition(appropriationQuery: Uint8Array) {
        if (appropriationQuery.length === 0) {
            this.openHint(Common.ErrorCodeMap[Common.ErrorHintMap.SettingPrintDataError]);
            return false;
        }

        this.isLoading = true;

        /* init */
        this.init();
        /* init */

        /* 取得列表 */
        await AppropriationModel.getReimbursement(appropriationQuery);
        const reimbursementList = AppropriationModel.getReimbursementList();
        this.reconciliationList = reimbursementList.list;
        /* 取得列表 */

        /* 取得展開資料 - map */
        const appropriationMapData = await AppropriationModel.getPrintAppropriationCollectionMap(appropriationQuery);
        console.log(appropriationMapData);
        this.countTotalFromMap(appropriationMapData.map);

        // 第一次預先展開
        this.unfoldList = [];
        Object.keys(appropriationMapData.map).forEach(
            (thisStoreCode) => {
                this.unfoldList.push(thisStoreCode);
            }
        );
        /* 取得展開資料 - map */

        /* 取得撥款彙總 */
        // await AppropriationModel.getAppropriationSummaryMap(appropriationQuery);
        // const appropriationSummaryMapData = AppropriationModel.getAppropriationSummaryMapData() as TCBReceiptApi.Protobuf.AppropriationSummaryMap;
        // this.countTotalBySummaryMap(appropriationSummaryMapData.map);
        const appropriationSummaryMapData = await AppropriationModel.getPrintAppropriationSummaryMap(appropriationQuery);
        this.countTotalBySummaryMap(appropriationSummaryMapData.map);
        /* 取得撥款彙總 */

        /* 取得卡別彙總 */
        // await AppropriationModel.getAppropriationSummaryByCardTypeCollectionMap(appropriationQuery);
        // const appropriationSummaryByCardTypeCollectionMapData = AppropriationModel.getAppropriationSummaryByCardTypeCollectionMapData() as TCBReceiptApi.Protobuf.AppropriationSummaryByCardTypeCollectionMap;

        // this.countTotalByCardTypeCollectionMap(appropriationSummaryByCardTypeCollectionMapData.map);
        const appropriationSummaryByCardTypeCollectionMapData = await AppropriationModel.getPrintAppropriationSummaryByCardTypeCollectionMap(appropriationQuery);
        this.countTotalByCardTypeCollectionMap(appropriationSummaryByCardTypeCollectionMapData.map);
        /* 取得卡別彙總 */

        /* 取得統計 */
        // 明細
        await AppropriationModel.getAppropriationTotal(appropriationQuery);
        const appropriationTotal = AppropriationModel.getAppropriationTotalData();
        this.countDetailTotal(appropriationTotal);

        // 撥款彙總
        await AppropriationModel.getAppropriationSummaryTotal(appropriationQuery);
        const appropriationSummaryTotal = AppropriationModel.getAppropriationSummaryTotalData();
        console.log("撥款彙總");
        console.log(appropriationSummaryTotal);
        this.appropriationTotalObj = appropriationSummaryTotal;

        // 卡別彙總
        const appropriationSummaryByCardTypeTotal = await AppropriationModel.getAppropriationSummaryByCardTypeCollectionTotal(appropriationQuery);
        console.log(appropriationSummaryByCardTypeTotal);
        this.cardTypeTotalObj = appropriationSummaryByCardTypeTotal;
        /* 取得統計 */

        this.isLoading = false;
    }
    /* 搜尋 */

    /* filter => 明細, 依撥款彙總, 依卡別彙總 */
    private async changeFilter(type: string) {
        this.filterType = type;
    }
    /* filter => 明細, 依撥款彙總, 依卡別彙總 */

    /* 展開收合 */
    private checkIsUnfold(storeCode: string) {
        let isUnfold = false;
        this.unfoldList.some((thisStoreCode) => {
            if (thisStoreCode === storeCode) {
                isUnfold = true;
                return true;
            }
        });
        return isUnfold;
    }

    // private async foldAndUnfoldItem(storeCode: string) {
    //     const isExist = this.unfoldList.findIndex(
    //         (thisStoreCode) => thisStoreCode === storeCode
    //     );
    //     if (isExist >= 0) {
    //         // 已經展開
    //         this.unfoldList.splice(isExist, 1);
    //     } else {
    //         // 判斷map是否有對應資料, 有 => 直接加入展開列表, 否 => 動態拿資料回來顯示
    //         if (!this.byCardMap[storeCode] || !this.byMotifyMap[storeCode] || !this.byAlipayMap[storeCode]
    //             || !this.byAppropriationMap[storeCode]
    //             || !this.cardTypeMap[storeCode] || !this.creditCardMap[storeCode]) {

    //             /* 整理condition */
    //             // 選取特店
    //             const item = TCBReceiptApi.Protobuf.AppropriationQuery.create();
    //             item.storeList = this.searchItem.storeArr; // 特店清單 - 輸入"特店代號""
    //             item.tidNumber = this.searchItem.tidNumber;
    //             // 左側
    //             item.dateType = this.searchItem.type; // 搜尋種類 [撥款日區間, 交易日, 結帳請款日]
    //             item.dateStart = this.searchItem.start; // 起
    //             item.dateEnd = this.searchItem.end; // 訖
    //             item.code = this.searchItem.transactionType; // 交易別
    //             item.flag = this.searchItem.noteType; // 註記
    //             // 右側
    //             item.cardType = this.searchItem.cardType.join(","); // 卡別
    //             item.cardNo = this.searchItem.cardNum; // 卡號
    //             item.orderNo = this.searchItem.orderNum; // 訂單編號
    //             item.terminalNoStart = this.searchItem.machineCodeStart; // 端末機號區間 起
    //             item.terminalNoEnd = this.searchItem.machineCodeEnd; // 端末機號區間 訖
    //             item.specificStore = storeCode; // 單一特店
    //             /* 整理condition */

    //             const appropriationQuery = TCBReceiptApi.Protobuf.AppropriationQuery.encode(item).finish();

    //             /* 取得展開資料 */
    //             // 明細
    //             await AppropriationModel.getAppropriation(appropriationQuery);
    //             const appropriation = AppropriationModel.getAppropriationData();
    //             const detailItem: { [k: string]: TCBReceiptApi.Protobuf.IAppropriation } = {};
    //             detailItem[storeCode] = appropriation;
    //             this.countTotalFromMap(detailItem);

    //             // 撥款彙總
    //             await AppropriationModel.getAppropriationSummary(appropriationQuery);
    //             const appropriationSummary = AppropriationModel.getAppropriationSummaryData();
    //             const appropriationItem: { [k: string]: TCBReceiptApi.Protobuf.IAppropriationSummaryTerminalNoMap } = {};
    //             appropriationItem[storeCode] = appropriationSummary;
    //             this.countTotalBySummaryMap(appropriationItem);

    //             // 卡別彙總
    //             await AppropriationModel.getAppropriationSummaryByCardTypeCollection(appropriationQuery);
    //             const appropriationSummaryByCardTypeCollection = AppropriationModel.getAppropriationSummaryByCardTypeCollectionData();

    //             const cardItem: { [k: string]: TCBReceiptApi.Protobuf.IAppropriationSummaryByCardTypeCollection } = {};
    //             cardItem[storeCode] = appropriationSummaryByCardTypeCollection;

    //             this.countTotalByCardTypeCollectionMap(cardItem);

    //             /* 取得展開資料 */
    //         }
    //         this.unfoldList.push(storeCode);
    //     }
    // }
    /* 展開收合 */

    /* 展開明細 */
    private showAll(storeCode: string) {

        const isExist = this.showAllList.findIndex(
            (thisStoreCode) => thisStoreCode === storeCode
        );

        if (isExist >= 0) {
            this.showAllList.splice(isExist, 1);
        } else {
            this.showAllList.push(storeCode);
        }
    }

    private IsShowAllItem(storeCode: string) {
        const isExist = this.showAllList.findIndex(
            (thisStoreCode) => thisStoreCode === storeCode
        );

        return isExist >= 0;
    }
    /* 展開明細 */

    /* 其他 */
    private isNotNatureNum(val: string) {
        if (val) {
            const num = (val + "").replace(/,/g, "");
            return Number(num) <= 0;
        } else {
            return false;
        }
    }

    // 數字轉千分位 (number to string) ps.保留數字小數點至第二位
    private convertNumberToMoney(num: number) {
        if (!num) {
            return 0;
        }
        let a: string = (num + "").replace(/,/g, "");
        if (a.indexOf(".") != -1) {
            if (a.split(".")[1].length > 2) {
                a = Math.round(+a * 100) / 100 + "";
            }
            if (a.split(".").length == 2) {
                return (+a.split(".")[0]).toLocaleString() + "." + a.split(".")[1];
            } else {
                return (+a.split(".")[0]).toLocaleString();
            }
        } else {
            return (+a.split(".")[0]).toLocaleString();
        }
    }

    // 統計 - 明細
    private countTotalFromMap(storeCodeMap: { [k: string]: TCBReceiptApi.Protobuf.IAppropriation; }) {
        if (storeCodeMap) {
            Object.keys(storeCodeMap).forEach(thisStoreCode => {
                /* 卡別 */
                const cardInfoList: (TCBReceiptApi.Protobuf.AppropriationInfoCredit | TotalItem)[] = []; // 紀錄卡別明細 => 每一條資料
                // 合計
                let countByCard = 0;
                let totalByCard = 0;

                const cardMenu = storeCodeMap[thisStoreCode].card as TCBReceiptApi.Protobuf.AppropriationCreditFirstMap;
                if (cardMenu && Object.keys(cardMenu).length > 0 && cardMenu.byFirstColumn) {
                    const terminalMap = cardMenu.byFirstColumn as { [k: string]: TCBReceiptApi.Protobuf.AppropriationCreditSecondMap };
                    if (terminalMap && Object.keys(terminalMap).length > 0) {
                        Object.keys(terminalMap).forEach(thisTerminal => {
                            // 端末機號小結
                            let terminalCount = 0;
                            let terminalTotal = 0;
    
                            const dateMap = terminalMap[thisTerminal].bySecondColumn as { [k: string]: TCBReceiptApi.Protobuf.AppropriationCreditBatchNoMap };
                            if (dateMap) {
                                Object.keys(dateMap).forEach(thisDate => {
                                    // 每日小結
                                    let dayTotal = 0;
    
                                    const batchNoMap = dateMap[thisDate].byBatchNo as { [k: string]: TCBReceiptApi.Protobuf.AppropriationCredit };
                                    if (batchNoMap) {
                                        Object.keys(batchNoMap).forEach(thisBatchNo => {
                                            const infoList = batchNoMap[thisBatchNo].list as TCBReceiptApi.Protobuf.AppropriationInfoCredit[];
                                            if (infoList) {
                                                // 批號小結
                                                const batchNoCount = infoList.length;
                                                let batchNoTotal = 0;
    
                                                infoList.forEach(thisInfo => {
                                                    // 每一條明細
                                                    cardInfoList.push(thisInfo);
                                                    // 統計 - 批號小結
                                                    batchNoTotal += (thisInfo.amount as number);
                                                    // 統計 - 每日小結
                                                    dayTotal += (thisInfo.amount as number);
                                                    // 統計 - 端末機號小結
                                                    terminalCount += 1;
                                                    terminalTotal += (thisInfo.amount as number);
                                                    // 統計 - 合計
                                                    countByCard += 1;
                                                    totalByCard += (thisInfo.amount as number);
                                                });
    
                                                const addItem: TotalItem = {
                                                    infoType: 2, // 種類 [2 => 批號小結, 3 => 端末機號小結, 4 => 合計]
    
                                                    batchNo: thisBatchNo, // 批號小結
                                                    subCount: batchNoCount, // 筆數
                                                    subTotal: batchNoTotal, // 小計
    
                                                    dayTotal: 0, // 每日小結
    
                                                    terminal: "", // 端末機號小結
                                                    terminalCount: 0, // 端末機號小結 - 筆數
                                                    terminalTotal: 0, // 端末機號小結 - 小計
    
                                                    count: 0, // 合計 - 筆數
                                                    total: 0, // 合計 - 總計
                                                };
                                                cardInfoList.push(addItem);
                                            }
                                        });
                                    }
    
                                    // 觀念 : 為了要讓每日小結與批號小結同層 在此無法使用push新增 而是對當前cardInfoList列表最後一筆做編輯 額外設定每日小結
                                    // 實作 : 直接使用arr.pop() 取得最後一組item且原先arr會被移除 再插入新整理好的item
                                    const lastItem: TotalItem = cardInfoList.pop() as TotalItem;
                                    if (lastItem) {
                                        const editItem: TotalItem = {
                                            infoType: 2, // 種類 [2 => 批號小結, 3 => 端末機號小結, 4 => 合計]
    
                                            batchNo: lastItem.batchNo, // 批號小結
                                            subCount: lastItem.subCount, // 筆數
                                            subTotal: lastItem.subTotal, // 小計
    
                                            dayTotal: dayTotal, // 每日小結
    
                                            terminal: "", // 端末機號小結
                                            terminalCount: lastItem.terminalCount, // 端末機號小結 - 筆數
                                            terminalTotal: lastItem.terminalTotal, // 端末機號小結 - 小計
    
                                            count: lastItem.count, // 合計 - 筆數
                                            total: lastItem.total, // 合計 - 總計
                                        };
                                        cardInfoList.push(editItem);
                                    }
                                });
                            }
    
                            const addItem: TotalItem = {
                                infoType: 3, // 種類 [2 => 批號小結, 3 => 端末機號小結, 4 => 合計]
    
                                batchNo: "", // 批號小結
                                subCount: 0, // 筆數
                                subTotal: 0, // 小計
    
                                dayTotal: 0, // 每日小結
    
                                terminal: thisTerminal, // 端末機號小結
                                terminalCount: terminalCount, // 端末機號小結 - 筆數
                                terminalTotal: terminalTotal, // 端末機號小結 - 小計
    
                                count: 0, // 合計 - 筆數
                                total: 0, // 合計 - 總計
                            };
                            cardInfoList.push(addItem);
                        });
    
                        // if (countByCard > 0) {
                        const addItem: TotalItem = {
                            infoType: 4, // 種類 [2 => 批號小結, 3 => 端末機號小結, 4 => 合計]
    
                            batchNo: "", // 批號小結
                            subCount: 0, // 筆數
                            subTotal: 0, // 小計
    
                            dayTotal: 0, // 每日小結
    
                            terminal: "", // 端末機號小結
                            terminalCount: 0, // 端末機號小結 - 筆數
                            terminalTotal: 0, // 端末機號小結 - 小計
    
                            count: countByCard, // 合計 - 筆數
                            total: totalByCard, // 合計 - 總計
                        };
                        cardInfoList.push(addItem);
                        // }
                    }
                }

                // 裝入卡別的map
                if (cardInfoList.length > 0) {
                    this.byCardMap[thisStoreCode] = cardInfoList; // 吃嚴謹的型別 => byCardMap也需要把TotalItem加入型別宣告
                    // this.$set(this.byCardMap, thisStoreCode, cardInfoList); // 就算型別不一致也能set在同一組object, 沒有嚴謹的規範
                }
                /* 卡別 */

                /* 支付寶 */
                const alipayInfoList: (TCBReceiptApi.Protobuf.AppropriationInfoAlipay | AlipayItem)[] = []; // 紀錄支付寶明細 => 每一條資料

                const alipayMenu = storeCodeMap[thisStoreCode].alipay as TCBReceiptApi.Protobuf.AppropriationAlipayDateMap;
                if (alipayMenu && alipayMenu.byDate) {
                    const dateMap = alipayMenu.byDate as { [k: string]: TCBReceiptApi.Protobuf.AppropriationAlipay };
                    if (dateMap) {
                        // let
                        let totalCount = 0;
                        let totalAbroadAmount = 0;
                        let totalAmount = 0;

                        Object.keys(dateMap).forEach(thisDate => {
                            const infoList = dateMap[thisDate].list as TCBReceiptApi.Protobuf.AppropriationInfoAlipay[];
                            if (infoList) {
                                const dateCount = infoList.length;
                                let dateAbroadAmount = 0;
                                let dateAmount = 0;

                                infoList.forEach(thisInfo => {
                                    // 每一條明細
                                    alipayInfoList.push(thisInfo);
                                    // 統計 - 每日小計
                                    dateAbroadAmount += (thisInfo.abroadAmount as number);
                                    dateAmount += (thisInfo.amount as number);
                                    // 統計 - 合計
                                    totalCount += 1;
                                    totalAbroadAmount += (thisInfo.abroadAmount as number);
                                    totalAmount += (thisInfo.amount as number);
                                });

                                const addItem: AlipayItem = {
                                    infoType: 2, // 種類 [2 => 小計, 3 => 合計]

                                    count: dateCount, // 筆數
                                    outAmount: dateAbroadAmount, // 境外交易金額
                                    amount: dateAmount, // 交易金額
                                };
                                alipayInfoList.push(addItem);
                            }
                        });

                        if (totalCount > 0) {
                            const addItem: AlipayItem = {
                                infoType: 3, // 種類 [2 => 小計, 3 => 合計]

                                count: totalCount, // 筆數
                                outAmount: totalAbroadAmount, // 境外交易金額
                                amount: totalAmount, // 交易金額
                            };
                            alipayInfoList.push(addItem);
                        }

                    }

                }
                // 裝入調帳的map
                if (alipayInfoList.length > 0) {
                    this.byAlipayMap[thisStoreCode] = alipayInfoList; // 吃嚴謹的型別 => byAlipayMap也需要把AlipayItem加入型別宣告
                }
                /* 支付寶 */

                /* 調帳 */
                const motifyInfoList: (TCBReceiptApi.Protobuf.AppropriationInfoMotify | MotifyItem)[] = []; // 紀錄調帳明細 => 每一條資料

                const motifyMenu = storeCodeMap[thisStoreCode].motify as TCBReceiptApi.Protobuf.AppropriationMotifyDateMap;
                if (motifyMenu && motifyMenu.byDate) {
                    const dateMap = motifyMenu.byDate as { [k: string]: TCBReceiptApi.Protobuf.AppropriationMotify };
                    if (dateMap) {
                        // let
                        let totalCount = 0;
                        let totalAmount = 0;
                        let totalCharge = 0;

                        Object.keys(dateMap).forEach(thisDate => {
                            const infoList = dateMap[thisDate].list as TCBReceiptApi.Protobuf.AppropriationInfoMotify[];
                            if (infoList) {
                                const dateCount = infoList.length;
                                let dateAmount = 0;
                                let dateCharge = 0;

                                infoList.forEach(thisInfo => {
                                    // 每一條明細
                                    motifyInfoList.push(thisInfo);
                                    // 統計 - 每日小計
                                    dateAmount += (thisInfo.motifyAmount as number);
                                    dateCharge += (thisInfo.motifyCharge as number);
                                    // 統計 - 合計
                                    totalCount += 1;
                                    totalAmount += (thisInfo.motifyAmount as number);
                                    totalCharge += (thisInfo.motifyCharge as number);
                                });

                                const addItem: MotifyItem = {
                                    infoType: 2, // 種類 [2 => 小計, 3 => 合計]

                                    count: dateCount, // 筆數
                                    amount: dateAmount, // 調整金額
                                    charge: dateCharge, // 調整手續費
                                };
                                motifyInfoList.push(addItem);
                            }
                        });

                        if (totalCount > 0) {
                            const addItem: MotifyItem = {
                                infoType: 3, // 種類 [2 => 小計, 3 => 合計]

                                count: totalCount, // 筆數
                                amount: totalAmount, // 調整金額
                                charge: totalCharge, // 調整手續費
                            };
                            motifyInfoList.push(addItem);
                        }
                    }
                }
                // 裝入調帳的map
                if (motifyInfoList.length > 0) {
                    this.byMotifyMap[thisStoreCode] = motifyInfoList; // 吃嚴謹的型別 => byMotifyMap也需要把MotifyItem加入型別宣告
                }
                /* 調帳 */

                /* 製作 - 撥款彙總的查看map */
                const cardTypeMenu = storeCodeMap[thisStoreCode].cardByType as TCBReceiptApi.Protobuf.AppropriationCreditFirstMap;
                if (cardTypeMenu && cardTypeMenu.byFirstColumn) {
                    const terminalMapByAppropriation = cardTypeMenu.byFirstColumn as { [k: string]: TCBReceiptApi.Protobuf.AppropriationCreditSecondMap };
                    if (cardTypeMenu && terminalMapByAppropriation) {
                        Object.keys(terminalMapByAppropriation).forEach(thisTerminal => {
                            const dateMap = terminalMapByAppropriation[thisTerminal].bySecondColumn as { [k: string]: TCBReceiptApi.Protobuf.AppropriationCreditBatchNoMap };
                            if (dateMap) {
                                Object.keys(dateMap).forEach(thisDate => {
                                    const batchNoMap = dateMap[thisDate].byBatchNo as { [k: string]: TCBReceiptApi.Protobuf.AppropriationCredit }; // 這邊的byBatchNo是類別註記
                                    if (batchNoMap) {
                                        Object.keys(batchNoMap).forEach(thisBatchNo => {
                                            const cardTypeList: AppropriationDetailInfo = []; // 紀錄同個類別註記下的明細 => 每一條資料
    
                                            const infoList = batchNoMap[thisBatchNo].list as TCBReceiptApi.Protobuf.AppropriationInfoCredit[];
                                            if (infoList) {
                                                // 類別註記
                                                const batchNoCount = infoList.length;
                                                let batchNoTotal = 0;
    
                                                infoList.forEach(thisInfo => {
                                                    // 每一條明細
                                                    cardTypeList.push(thisInfo);
                                                    // 統計 - 類別註記
                                                    batchNoTotal += (thisInfo.amount as number);
                                                });
    
                                                if (cardTypeList.length > 0) {
                                                    const addItem: AppropriationDetailTotal = {
                                                        infoType: 2, // [1 => 一般資料, 2 => 小計]
                                                        terminalNo: thisTerminal,
                                                        count: batchNoCount,
                                                        total: batchNoTotal,
                                                    };
                                                    cardTypeList.push(addItem);
    
                                                    if (!this.appropriationDetail[thisStoreCode]) {
                                                        this.appropriationDetail[thisStoreCode] = {};
                                                    }
                                                    if (!this.appropriationDetail[thisStoreCode][thisTerminal]) {
                                                        this.appropriationDetail[thisStoreCode][thisTerminal] = {};
                                                    }
                                                    if (!this.appropriationDetail[thisStoreCode][thisTerminal][thisDate]) {
                                                        this.appropriationDetail[thisStoreCode][thisTerminal][thisDate] = {};
                                                    }
    
                                                    this.appropriationDetail[thisStoreCode][thisTerminal][thisDate][thisBatchNo] = cardTypeList;
                                                }
                                            }
                                        });
                                    }
                                });
                            }
                        });
                    }
                }

                /* 製作 - 撥款彙總的查看map */
            });
        }
    }

    // 統計 - 撥款彙總
    private countTotalBySummaryMap(storeCodeMap: { [k: string]: TCBReceiptApi.Protobuf.IAppropriationSummaryTerminalNoMap; }) {
        if (storeCodeMap) {
            Object.keys(storeCodeMap).forEach(thisStoreCode => {
                /* 撥款 */
                const appropriationInfoList: TCBReceiptApi.Protobuf.AppropriationInfoSummary[] = []; // 紀錄撥款明細 => 每一條資料
                // 合計
                const addTotalItem = TCBReceiptApi.Protobuf.AppropriationInfoSummary.create({ infoType: 3 });

                const terminalMap = storeCodeMap[thisStoreCode].byTerminalNo as { [k: string]: TCBReceiptApi.Protobuf.AppropriationSummaryDateMap };
                if (terminalMap) {
                    Object.keys(terminalMap).forEach(thisTerminal => {
                        const dateMap = terminalMap[thisTerminal].byDate as { [k: string]: TCBReceiptApi.Protobuf.AppropriationSummaryTypeFlagMap };
                        if (dateMap) {
                            Object.keys(dateMap).forEach(thisDate => {
                                const cardTypeMenu = dateMap[thisDate].byTypeFlag as { [k: string]: TCBReceiptApi.Protobuf.AppropriationSummary };
                                if (cardTypeMenu) {
                                    Object.keys(cardTypeMenu).forEach(thisCardType => {
                                        const infoList = cardTypeMenu[thisCardType].list as TCBReceiptApi.Protobuf.AppropriationInfoSummary[];
                                        if (infoList) {
                                            const addItem = TCBReceiptApi.Protobuf.AppropriationInfoSummary.create({ infoType: 2 });

                                            infoList.forEach(thisInfo => {
                                                // 每一條明細
                                                appropriationInfoList.push(thisInfo);
                                                // 小計
                                                addItem.totalCount += (thisInfo.totalCount as number);
                                                addItem.totalAmount += (thisInfo.totalAmount as number);
                                                addItem.salesCount += (thisInfo.salesCount as number);
                                                addItem.salesAmount += (thisInfo.salesAmount as number);
                                                addItem.returnCount += (thisInfo.returnCount as number);
                                                addItem.returnAmount += (thisInfo.returnAmount as number);
                                                addItem.charge += (thisInfo.charge as number);
                                                addItem.ifr += (thisInfo.ifr as number);
                                                addItem.motifyAmount += (thisInfo.motifyAmount as number);
                                                addItem.motifyCharge += (thisInfo.motifyCharge as number);
                                                addItem.installRate += (thisInfo.installRate as number);
                                                addItem.chargeRate += (thisInfo.chargeRate as number);
                                                addItem.netAmount += (thisInfo.netAmount as number);
                                                // 合計
                                                addTotalItem.totalCount += (thisInfo.totalCount as number);
                                                addTotalItem.totalAmount += (thisInfo.totalAmount as number);
                                                addTotalItem.salesCount += (thisInfo.salesCount as number);
                                                addTotalItem.salesAmount += (thisInfo.salesAmount as number);
                                                addTotalItem.returnCount += (thisInfo.returnCount as number);
                                                addTotalItem.returnAmount += (thisInfo.returnAmount as number);
                                                addTotalItem.charge += (thisInfo.charge as number);
                                                addTotalItem.ifr += (thisInfo.ifr as number);
                                                addTotalItem.motifyAmount += (thisInfo.motifyAmount as number);
                                                addTotalItem.motifyCharge += (thisInfo.motifyCharge as number);
                                                addTotalItem.installRate += (thisInfo.installRate as number);
                                                addTotalItem.chargeRate += (thisInfo.chargeRate as number);
                                                addTotalItem.netAmount += (thisInfo.netAmount as number);
                                            });

                                            appropriationInfoList.push(addItem); // 小計
                                        }
                                    });
                                }
                            });
                        }
                    });
                }

                // 合計 並 裝上map
                // if (appropriationInfoList.length > 0) {
                appropriationInfoList.push(addTotalItem); // 合計

                // 裝入撥款的map
                this.byAppropriationMap[thisStoreCode] = appropriationInfoList; // 吃嚴謹的型別
                // }
            });
        }
    }

    // 統計 - 卡別彙總
    private countTotalByCardTypeCollectionMap(storeCodeMap: { [k: string]: TCBReceiptApi.Protobuf.IAppropriationSummaryByCardTypeCollection; }) {
        if (storeCodeMap) {
            Object.keys(storeCodeMap).forEach(thisStoreCode => {
                /* 卡別彙整 */
                const cardMenu = storeCodeMap[thisStoreCode].cardType as TCBReceiptApi.Protobuf.AppropriationSummaryByCardType;
                if (cardMenu) {
                    const visa = cardMenu.visa as TCBReceiptApi.Protobuf.AppropriationInfoSummaryByCardType;
                    const mc = cardMenu.mc as TCBReceiptApi.Protobuf.AppropriationInfoSummaryByCardType;
                    const jcb = cardMenu.jcb as TCBReceiptApi.Protobuf.AppropriationInfoSummaryByCardType;
                    const cup = cardMenu.cup as TCBReceiptApi.Protobuf.AppropriationInfoSummaryByCardType;
                    const fisc = cardMenu.fisc as TCBReceiptApi.Protobuf.AppropriationInfoSummaryByCardType;
                    const motify = cardMenu.motify as TCBReceiptApi.Protobuf.AppropriationInfoSummaryByCardType;

                    // 合計 = VISA + MasterCard + JCB + 銀聯卡 + 金融卡 + 調帳
                    const totalByCard = TCBReceiptApi.Protobuf.AppropriationInfoSummaryByCardType.create({
                        ourBankCount: visa.ourBankCount + mc.ourBankCount + jcb.ourBankCount + cup.ourBankCount + fisc.ourBankCount + motify.ourBankCount,
                        ourBankAmount: visa.ourBankAmount + mc.ourBankAmount + jcb.ourBankAmount + cup.ourBankAmount + fisc.ourBankAmount + motify.ourBankAmount,
                        notOurBankCount: visa.notOurBankCount + mc.notOurBankCount + jcb.notOurBankCount + cup.notOurBankCount + fisc.notOurBankCount + motify.notOurBankCount,
                        notOurBankAmount: visa.notOurBankAmount + mc.notOurBankAmount + jcb.notOurBankAmount + cup.notOurBankAmount + fisc.notOurBankAmount + motify.notOurBankAmount,
                        totalCount: visa.totalCount + mc.totalCount + jcb.totalCount + cup.totalCount + fisc.totalCount + motify.totalCount,
                        totalAmount: visa.totalAmount + mc.totalAmount + jcb.totalAmount + cup.totalAmount + fisc.totalAmount + motify.totalAmount,
                    });
                    this.$set(cardMenu, "total", totalByCard);

                    this.cardTypeMap[thisStoreCode] = cardMenu;
                }

                /* 卡別彙整 */

                /* 發卡行交易統計-信用卡/銀聯卡 */
                const creditCardInfoList: (TCBReceiptApi.Protobuf.AppropriationInfoSummaryByIssuer | { count: number, amount: number, infoType: number })[] = []; // 紀錄信用卡/銀聯卡明細 => 每一條資料

                const creditCardMenu = storeCodeMap[thisStoreCode].creditCard as TCBReceiptApi.Protobuf.AppropriationSummaryByIssuer;
                if (creditCardMenu) {
                    const infoList = creditCardMenu.list as TCBReceiptApi.Protobuf.AppropriationInfoSummaryByIssuer[];
                    if (infoList) {
                        // 合計
                        let count = 0;
                        let amount = 0;

                        infoList.forEach(thisInfo => {
                            // 每一條明細
                            creditCardInfoList.push(thisInfo);
                            // 合計
                            count += (thisInfo.count as number);
                            amount += (thisInfo.amount as number);
                        });

                        const addInfo: { count: number, amount: number, infoType: number } = {
                            count: count,
                            amount: amount,
                            infoType: 2, // 註記
                        };
                        creditCardInfoList.push(addInfo);

                        if (count > 0) {
                            this.creditCardMap[thisStoreCode] = creditCardInfoList;
                        }
                    }
                }
                /* 發卡行交易統計-信用卡/銀聯卡 */

                /* 發卡行交易統計-金融卡 */
                const debitCardInfoList: (TCBReceiptApi.Protobuf.AppropriationInfoSummaryByIssuer | { count: number, amount: number, infoType: number })[] = []; // 紀錄金融卡明細 => 每一條資料

                const debitCardMenu = storeCodeMap[thisStoreCode].debitCard as TCBReceiptApi.Protobuf.AppropriationSummaryByIssuer;
                if (debitCardMenu) {
                    const infoList = debitCardMenu.list as TCBReceiptApi.Protobuf.AppropriationInfoSummaryByIssuer[];
                    if (infoList) {
                        // 合計
                        let count = 0;
                        let amount = 0;

                        infoList.forEach(thisInfo => {
                            // 每一條明細
                            debitCardInfoList.push(thisInfo);
                            // 合計
                            count += (thisInfo.count as number);
                            amount += (thisInfo.amount as number);
                        });

                        const addInfo: { count: number, amount: number, infoType: number } = {
                            count: count,
                            amount: amount,
                            infoType: 2, // 註記
                        };
                        debitCardInfoList.push(addInfo);

                        if (count > 0) {
                            this.debitCardMap[thisStoreCode] = debitCardInfoList;
                        }
                    }
                }
                /* 發卡行交易統計-金融卡 */
            });
        }
    }

    // 統計 明細 總計 (把明細 + 支付寶 + 調帳)
    private countDetailTotal(map: TCBReceiptApi.Protobuf.AppropriationTotal) {
        let count = 0;
        let total = 0;

        // 明細
        if (map.card) {
            if (map.card.count) {
                count += map.card.count;
            }
            if (map.card.total) {
                total += map.card.total;
            }
        }
        // 支付寶
        if (map.alipay) {
            if (map.alipay.count) {
                count += map.alipay.count;
            }
            if (map.alipay.total) {
                total += map.alipay.total;
            }
        }
        // 調帳
        if (map.motify) {
            if (map.motify.count) {
                count += map.motify.count;
            }
            if (map.motify.total) {
                total += map.motify.total;
            }
        }

        this.detailTotalObj.count = count;
        this.detailTotalObj.total = total;
    }
    /* 其他 */

    /* 取得最新的權限map並判斷有何權限 */
    private async setPriv() {
        const meta = this.$route.meta as { privKey: { controller: string, method: string, type: string } };
        console.log(meta);

        const isSetPriv = await Priv._checkPriv(meta.privKey);
        if (!isSetPriv) {
            this.$router.push(DefaultPath);  // priv
        }

        if (!Priv._checkPrivType(meta.privKey.type)) {
            this.$router.push(DefaultPath);  // priv
        }
    }
    /* 取得最新的權限map並判斷有何權限 */

    private init() {
        this.byCardMap = {};
        this.byAlipayMap = {};
        this.byMotifyMap = {};
        this.appropriationDetail = {};
        this.byAppropriationMap = {};
        this.cardTypeMap = {};
        this.creditCardMap = {};
        this.debitCardMap = {};
    }
}
